// R-feat-2026-10-17 U4 易用性:友好错误 Modal + Context
//
// 设计:
//   - 替代「message.error 弹完用户什么也不能做」的反模式。
//   - 5xx / 网络错误 自动跳出「系统繁忙,请稍后重试 [重试] [取消]」Modal,
//     而不是裸 toast。
//   - 4xx 仍走 message.error(用户输入校验,不需要 retry)。
//
// 用法:
//   const { showError } = useFriendlyError()
//   showError({ message: '...', onRetry: () => mutate() })

import { createContext, useContext, useMemo, useRef, useState, useCallback } from 'react'
import { Modal, Button, Space, Typography, Collapse } from 'antd'
import { ReloadOutlined, CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons'

const FriendlyErrorContext = createContext(null)

function getHttpStatus(err) {
  return err?.response?.status ?? null
}

export function FriendlyErrorProvider({ children }) {
  // 用 ref 暂存 onRetry(state 闭包陷阱:user 点击时若 state 已变,旧 retry 可能闭包旧变量)
  const [state, setState] = useState({
    open: false,
    title: '',
    message: '',
    requestId: '',
    details: '',
    onRetry: null,
    retryLabel: '重试',
    status: null,
  })
  const retryRef = useRef(null)
  const [busy, setBusy] = useState(false)

  const showError = useCallback((opts) => {
    if (!opts) return
    retryRef.current = opts.onRetry || null
    setState({
      open: true,
      title: opts.title || (opts.status >= 500 ? '系统繁忙' : '操作失败'),
      message: opts.message || '请稍后重试。如果问题持续存在,请联系管理员。',
      requestId: opts.requestId || '',
      details: opts.details || '',
      onRetry: opts.onRetry || null,
      retryLabel: opts.retryLabel || '重试',
      status: opts.status ?? null,
    })
  }, [])

  const close = useCallback(() => {
    setState(s => ({ ...s, open: false }))
    retryRef.current = null
    setBusy(false)
  }, [])

  const handleRetry = useCallback(async () => {
    const fn = retryRef.current
    if (typeof fn !== 'function') return
    setBusy(true)
    try {
      await fn()
      close()
    } catch (_) {
      // 仍失败,保留 Modal 让用户看到「还是不行」,他自己会点关闭
      setBusy(false)
    }
  }, [close])

  const ctx = useMemo(() => ({ showError, close }), [showError, close])

  return (
    <FriendlyErrorContext.Provider value={ctx}>
      {children}
      <Modal
        open={state.open}
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
            <span>{state.title}</span>
          </Space>
        }
        onCancel={close}
        closable={!busy}
        maskClosable={false}
        keyboard={false}
        width={460}
        footer={[
          // 前端规范 §1.2 弹窗按钮文案:用「取消」,不用「关闭」(X 才是关闭按钮)
          <Button key="cancel" onClick={close} icon={<CloseOutlined />} aria-label="关闭">
            取消
          </Button>,
          state.onRetry && (
            <Button
              key="retry"
              type="primary"
              loading={busy}
              onClick={handleRetry}
              icon={<ReloadOutlined />}
            >
              {state.retryLabel}
            </Button>
          ),
        ].filter(Boolean)}
      >
        <Typography.Paragraph style={{ marginBottom: state.requestId || state.details ? 12 : 0 }}>
          {state.message}
        </Typography.Paragraph>
        {(state.requestId || state.details) && (
          <Collapse
            ghost
            size="small"
            items={[{
              key: 'detail',
              label: '技术详情',
              children: (
                <div style={{ fontSize: 12, color: '#666' }}>
                  {state.requestId && (
                    <div style={{ marginBottom: 4 }}>
                      <strong>Request ID:</strong> <code>{state.requestId}</code>
                    </div>
                  )}
                  {state.status != null && (
                    <div style={{ marginBottom: 4 }}>
                      <strong>HTTP 状态:</strong> {state.status}
                    </div>
                  )}
                  {state.details && (
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                      {state.details}
                    </pre>
                  )}
                </div>
              ),
            }]}
          />
        )}
      </Modal>
    </FriendlyErrorContext.Provider>
  )
}

export function useFriendlyError() {
  const ctx = useContext(FriendlyErrorContext)
  if (!ctx) {
    // 未挂 provider 时降级为 no-op,避免 useApiMutation 调用崩溃
    return {
      showError: () => {},
      close: () => {},
      getHttpStatus,
    }
  }
  return { ...ctx, getHttpStatus }
}
